import * as XLSX from 'xlsx';

import { BulkCategoryRow } from '~/services/ExperiencesService';

export type ParseResult = {
  rows: BulkCategoryRow[];
  errors: Array<{ row: number; message: string }>;
};

function parseCategoryTags(raw: string): string[] {
  if (!raw) return [];
  // Support both `"Cat 1","Cat 2"` and plain comma-separated `Cat 1,Cat 2`
  const quoted = raw.match(/"([^"]+)"/g);
  if (quoted) {
    return quoted.map((s) => s.replace(/"/g, '').trim()).filter(Boolean);
  }
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function parseExperienceCategoryFile(file: File): Promise<ParseResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawRows: Array<Record<string, string>> = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        const rows: BulkCategoryRow[] = [];
        const errors: Array<{ row: number; message: string }> = [];

        rawRows.forEach((raw, idx) => {
          const rowNum = idx + 2; // 1-indexed + header row
          const offerId = (raw['DEAL ID'] ?? '').toString().trim();
          const categoryTagsRaw = (raw['CATEGORY TAGS'] ?? '').toString().trim();
          const action = (raw['ACTION'] ?? '').toString().trim().toLowerCase();

          if (!offerId) {
            errors.push({ row: rowNum, message: 'Missing DEAL ID' });
            return;
          }

          if (action !== 'add' && action !== 'remove') {
            errors.push({ row: rowNum, message: `Invalid ACTION "${action}" — must be "add" or "remove"` });
            return;
          }

          const categories = parseCategoryTags(categoryTagsRaw);
          if (categories.length === 0) {
            errors.push({ row: rowNum, message: 'At least one CATEGORY TAG is required' });
            return;
          }

          rows.push({ offerId, categories, action: action as 'add' | 'remove' });
        });

        resolve({ rows, errors });
      } catch (err) {
        reject(new Error(`Failed to parse file: ${err instanceof Error ? err.message : String(err)}`));
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}
